import React, { useState, useEffect } from "react"
import styled from "styled-components"
import Product from "./product"
import Icon from "../components/icon"
import { Collapse } from "react-collapse"
import ReactMarkdown from "react-markdown"
import { theme } from "../style/theme"

//What is visible when collapsed on mobile. Includes category name and chips. Has click handler.
const MobileContainer = styled.section`
    margin-top: 1rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
`
//E.G Good, better, best.
const CategoryName = styled.h4`
    color: ${props =>
        props.active
            ? props => props.theme.primary
            : props => props.theme.grey};
    transform: translateY(1px);
    padding: 0 2px;
    font-size: 0.8rem;
    @media screen and (max-width: 767px) {
        min-width: 2.45rem;
        font-size: 0.6rem;
    }
`
const CategoryContainer = styled.div`
    width: 100%;
    display: flex;
    height: 1.25rem;
    align-items: center;
    flex-wrap: nowrap;
    @media screen and (min-width: 768px) {
        width: auto;
        margin-right: 0.5rem;
        margin-top: 0.5rem;
        height: auto;
    }
`
const CategoryContainerDesktop = styled(CategoryContainer)`
    display: none;
    @media screen and (min-width: 768px) {
        display: flex;
        margin: 4px 0;
    }
`
//Square product names on mobile
const Chip = styled.div`
    border: 1px solid ${props => props.theme.grey};
    display: inline;
    font-size: 0.6rem;
    text-transform: uppercase;
    font-family: "Fira Sans Extra Condensed";
    letter-spacing: 0px;
    color: ${props => props.theme.text};
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 4px;
    margin-right: 5px;
    margin-bottom: 5px;
`
const ChipContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    height: 100%;
`
//Series name, description, etc
export const SeriesHeader = styled.div`
    display: flex;
    padding: 0.5rem 1rem;
    border-bottom: 0.5px solid ${props => props.theme.lightGrey};
    position: relative;
    max-height: 7.5rem;
    h1 {
        font-family: "Domine";
        text-transform: none;
        font-size: 1.25rem;
        letter-spacing: 0px;
        font-weight: 400;
    }
    img {
        width: 50px;
        object-fit: contain;
        object-position: top;
        margin-right: 0.75rem;
    }
    @media screen and (min-width: 768px) {
        max-height: unset;
        border-bottom: none;
        padding: 0 0 8px 0;
        h1 {
            font-size: 1.3rem;
        }
        header {
            padding: 0 1.5rem;
            margin-bottom: 0.5rem;
        }
        img {
            width: 3.5rem;
            margin-top: -1px;
            filter: drop-shadow(1px 2px 3px rgba(0, 0, 0, 0.4));
        }
    }
`

const StyledIcon = styled(Icon)`
    padding: 0.5rem;
    right: 0.25rem;
    position: absolute;
`

//Layout control for nested products
const ProductsContainer = styled.div`
    @media screen and (min-width: 768px) {
        padding-left: 5rem;
    }
`

//This component shifts content down on extra small screen sizes, in case the product chips wrap.
const CollapseWrapper = styled.div`
    @media screen and (min-width: 768px) {
        border-bottom: 0.5px solid #c9cfd7;
        padding-bottom: 0.5rem;
    }
    @media screen and (max-width: 383px) {
        margin-top: ${props => (props.willShift ? "2rem" : null)};
    }
`

export default function Series({
    data,
    images,
    desktop,
    products,
    getCurrentMarket,
    currentMarket,
    marketCategory,
}) {
    //Initial state. Always expanded on desktop
    const [expanded, setExpanded] = useState(desktop)

    //Associate category names with series names
    const categories = ["good", "better", "best"]
    let category
    switch (data.name) {
        case "Garden Series™":
            category = "good"
            break
        case "Premier Series™":
            category = "better"
            break
        case "Premier PRO Series™":
            category = "best"
            break
        default:
            break
    }

    //Expand and collapse categories on mobile
    function handleClick(e) {
        e.preventDefault()
        setExpanded(!expanded)
    }

    useEffect(() => {
        //Fixes mobile safari not displaying correct height of series headers
        let headers = document.querySelectorAll(".series-header")
        for (let i = 0; i < headers.length; i++) {
            headers[i].style.maxHeight = headers[i].offsetHeight + 36 + "px"
        }

        //Expand all panels @ desktop breakpoint
        if (desktop && !expanded) {
            setExpanded(true)
        }
    }, [desktop, expanded])

    //Modifies names to fit in chips. E.G Premiere Pro Ranch becomes Pro Ranch.
    function shortenName(name) {
        let split = name.split(" ")
        if (split[0] === "Premier" && split[1] === "Pro") {
            return name.split("Premier ")[1]
        } else return name
    }

    return (
        <React.Fragment>
            {!desktop ? (
                <MobileContainer onClick={e => handleClick(e)}>
                    <Icon color={theme.gold} size={18}>
                        star_rate
                    </Icon>
                    <CategoryContainer>
                        <CategoryName active>{category}</CategoryName>
                        <ChipContainer>
                            {data.products.map((el, key) => {
                                return (
                                    <Chip key={key}>
                                        {shortenName(el.name)}
                                    </Chip>
                                )
                            })}
                        </ChipContainer>
                    </CategoryContainer>
                    <StyledIcon
                        color={expanded ? theme.primary : null}
                        size={20}
                    >
                        {expanded ? "remove" : "add"}
                    </StyledIcon>
                </MobileContainer>
            ) : null}
            <CollapseWrapper
                willShift={data.products.length > 2}
                expanded={expanded}
            >
                <Collapse isOpened={expanded}>
                    <SeriesHeader>
                        <img
                            srcSet={images[data.image]}
                            alt={`Tuff Shed ${data.name} warranty`}
                            height="85"
                        />
                        <header className="series-header">
                            <CategoryContainerDesktop>
                                {desktop
                                    ? categories.map(el => {
                                          return (
                                              <CategoryContainer key={el}>
                                                  <Icon
                                                      color={theme.gold}
                                                      size={18}
                                                  >
                                                      star_rate
                                                  </Icon>
                                                  <CategoryName
                                                      active={el === category}
                                                  >
                                                      {el}
                                                  </CategoryName>
                                              </CategoryContainer>
                                          )
                                      })
                                    : null}
                            </CategoryContainerDesktop>
                            <h1>{data.name}</h1>
                            <ReactMarkdown source={data.description} />
                        </header>
                    </SeriesHeader>
                    <ProductsContainer>
                        {data.products.map((el, key) => {
                            return (
                                <Product
                                    warrantyImage={images[data.image]}
                                    SeriesHeader={SeriesHeader}
                                    seriesName={data.name}
                                    data={el}
                                    images={images}
                                    key={key}
                                    products={products[el.name]}
                                    getCurrentMarket={getCurrentMarket}
                                    currentMarket={currentMarket}
                                    marketCategory={marketCategory}
                                />
                            )
                        })}
                    </ProductsContainer>
                </Collapse>
            </CollapseWrapper>
        </React.Fragment>
    )
}
